import React from 'react';
import {
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
  IconsSection,
  StyledIconButton,
} from 'styles/v2/Styles.styled';
import ElectricityGrid from './ElectricityGrid';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { AddCustomBlockButton } from 'components/v2/buttons/AddCustomBlockButton';
import ToggleButton from '../../panels/toggleable/ToggleButton';
import usePcrV2 from '../../hooks/usePcrV2';
import FontIcon from 'components/icons/FontIcon';
import { useGetEpdElectricity } from 'services/api/queries';
import { useCreateEpdElectricity, useDeleteEpdElectricity } from 'services/api/mutations';
import { ElectricityDataModel } from 'types/types';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';

const ElectricityList: React.FunctionComponent<{
  epdVersionId: string;
  validationState: any;
}> = ({ epdVersionId, validationState }) => {
  const electricityItems = useGetEpdElectricity(epdVersionId).data;
  const createElectricityMutation = useCreateEpdElectricity(epdVersionId);
  const deleteElectricityMutation = useDeleteEpdElectricity(epdVersionId);
  const isPcrV2 = usePcrV2();
  const isReadOnly = useIsReadOnlyMode();

  const getElectricityHeaderText = (electricityUsedInProcessName: string | undefined, isDefaultElectricity: boolean) => {
    if (!isPcrV2) return 'Electricity used in the manufacturing process in A3';
    if (isDefaultElectricity) return 'Electricity used in the manufacturing process in A3 (A5 for services)';
    if (electricityUsedInProcessName) return `Electricity used in ${electricityUsedInProcessName}`;
    return 'Electricity used in unnamed process';
  };

  const getElectricityErrors = (validationState: any, index: number) => {
    return validationState.errors[`electricityItems[${index}]`];
  };

  const confirmDeleteElectricity = (electricity: ElectricityDataModel) => {
    const electricityName = electricity.electricityUsedInProcessName
      ? `Electricity used in ${electricity.electricityUsedInProcessName}`
      : 'Electricity used in unnamed process';
    confirmDelete({
      caption: `Are you sure you want to delete ${electricityName}?`,
      onConfirm: () => deleteElectricityMutation.mutate(electricity.id!),
    });
  };

  const headerModuleTemplate = (options: any, headerText: string, electricity: ElectricityDataModel, errors?: any) => {
    return (
      <div className={options.className}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{headerText}</HeaderCaptionSemiBold>
          <TooltipHelpIcon
            content={
              electricity.isDefault
                ? 'The energy source behind electricity used in the manufacturing process in A3 and its climate impact as kg CO₂ eq./kWh (using the GWP-GHG indicator)'
                : 'Add information on how electricity has been modelled for other upstream and downstream processes, if relevant and available. Specify the process under "Electricity used in".'
            }
          />
          {errors?.some((error: any) => !!error) && (
            <TooltipErrorIcon content={'Required'} containerStyle={{ position: 'relative' }} />
          )}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
        {!electricity.isDefault && !isReadOnly && (
          <IconsSection>
            <StyledIconButton icon={<FontIcon icon="delete" />} text onClick={() => confirmDeleteElectricity(electricity)} />
          </IconsSection>
        )}
      </div>
    );
  };

  const hasElectricityItems = Array.isArray(electricityItems) && electricityItems.length > 0;

  if (!hasElectricityItems) return null;

  return (
    <>
      {electricityItems.map((electricityItem: ElectricityDataModel, index: number) => {
        const { electricityUsedInProcessName, isDefault } = electricityItem;
        const headerText = getElectricityHeaderText(electricityUsedInProcessName, isDefault);
        return (
          <ExpandablePanelModule
            key={electricityItem.ord}
            headerTemplate={(options) =>
              headerModuleTemplate(options, headerText, electricityItem, [getElectricityErrors(validationState, index)])
            }
            toggleable
          >
            <ElectricityGrid
              epdVersionId={epdVersionId}
              headerText={headerText}
              data={electricityItem}
              errors={getElectricityErrors(validationState, index)}
              isPcrV2={isPcrV2}
              isReadOnly={isReadOnly}
            />
          </ExpandablePanelModule>
        );
      })}
      {isPcrV2 && (
        <AddCustomBlockButton
          onClick={() =>
            createElectricityMutation.mutate({
              epdVersionId,
              ord: electricityItems.length + 1,
              isDefault: false,
            })
          }
          caption="Add additional electricity table"
        />
      )}
    </>
  );
};

export default ElectricityList;
